<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'

const props = defineProps({
  teamId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['player-selected', 'player-cleared'])

// Depth chart state
const depthChartData = ref(null)
const isLoadingDepthChart = ref(false)
const showPlayerSelectionModal = ref(false)
const selectedPosition = ref(null)
const selectedRole = ref(null)
const teamData = ref(null)
const isLoadingTeam = ref(false)

// Helper function to create initial depth chart structure (local only, not saved)
const createInitialDepthChartStructure = (teamId) => {
  return {
    'starter': {
      id: null,
      team_id: teamId,
      role: 'Starter',
      pg_players: null,
      sg_players: null,
      sf_players: null,
      pf_players: null,
      c_players: null,
      role_order: 1,
      is_active: 1
    },
    'rotational': {
      id: null,
      team_id: teamId,
      role: 'Rotational Player',
      pg_players: null,
      sg_players: null,
      sf_players: null,
      pf_players: null,
      c_players: null,
      role_order: 2,
      is_active: 1
    },
    'injury_backup': {
      id: null,
      team_id: teamId,
      role: 'Injury Backup',
      pg_players: null,
      sg_players: null,
      sf_players: null,
      pf_players: null,
      c_players: null,
      role_order: 3,
      is_active: 1
    },
    'injury_backup_2': {
      id: null,
      team_id: teamId,
      role: 'Injury Backup 2',
      pg_players: null,
      sg_players: null,
      sf_players: null,
      pf_players: null,
      c_players: null,
      role_order: 4,
      is_active: 1
    },
    'injury_backup_3': {
      id: null,
      team_id: teamId,
      role: 'Injury Backup 3',
      pg_players: null,
      sg_players: null,
      sf_players: null,
      pf_players: null,
      c_players: null,
      role_order: 5,
      is_active: 1
    },
    'in_out_rotation': {
      id: null,
      team_id: teamId,
      role: 'In & Out of Rotation',
      pg_players: null,
      sg_players: null,
      sf_players: null,
      pf_players: null,
      c_players: null,
      role_order: 6,
      is_active: 1
    },
    'out_rotation': {
      id: null,
      team_id: teamId,
      role: 'Out of Rotation',
      pg_players: null,
      sg_players: null,
      sf_players: null,
      pf_players: null,
      c_players: null,
      role_order: 7,
      is_active: 1
    }
  }
}

// Fetch team data with roster
const fetchTeamData = async () => {
  if (!props.teamId) {
    teamData.value = null
    return
  }
  
  isLoadingTeam.value = true
  try {
    const response = await axios.get(`/api/v2/team/${props.teamId}`)
    teamData.value = response.data
    await fetchDepthChartData(props.teamId)
  } catch (error) {
    console.error('Error fetching team data:', error)
    teamData.value = null
  } finally {
    isLoadingTeam.value = false
  }
}

// Fetch depth chart data (read-only, for display)
const fetchDepthChartData = async (teamId) => {
  if (!teamId) {
    depthChartData.value = null
    return
  }
  
  isLoadingDepthChart.value = true
  try {
    const response = await axios.get(`/api/v2/projections-settings/teams/depth-chart/${teamId}`)
    
    if (!response.data || response.data.length === 0) {
      // Create local structure only, don't save to backend
      depthChartData.value = createInitialDepthChartStructure(teamId)
    } else if (Array.isArray(response.data)) {
      const transformedData = {}
      
      response.data.forEach(roleData => {
        const role = roleData.role.toLowerCase().replace(/\s+/g, '_')
        
        const roleMapping = {
          'starter': 'starter',
          'rotational_player': 'rotational',
          'injury_backup': 'injury_backup',
          'injury_backup_2': 'injury_backup_2',
          'injury_backup_3': 'injury_backup_3',
          'in_&_out_of_rotation': 'in_out_rotation',
          'out_of_rotation': 'out_rotation'
        }
        
        const mappedRole = roleMapping[role] || role
        
        transformedData[mappedRole] = {
          id: roleData.id,
          team_id: roleData.team_id,
          role: roleData.role,
          pg_players: roleData.pg_players,
          sg_players: roleData.sg_players,
          sf_players: roleData.sf_players,
          pf_players: roleData.pf_players,
          c_players: roleData.c_players,
          role_order: roleData.role_order,
          is_active: roleData.is_active
        }
      })
      
      depthChartData.value = transformedData
    } else {
      depthChartData.value = null
    }
  } catch (error) {
    console.error('Error fetching depth chart data:', error)
    if (error.response?.status === 404) {
      // Create local structure only, don't save to backend
      depthChartData.value = createInitialDepthChartStructure(teamId)
    } else {
      depthChartData.value = null
    }
  } finally {
    isLoadingDepthChart.value = false
  }
}

// Get depth chart player name
const getDepthChartPlayer = (position, role) => {
  if (!depthChartData.value || !depthChartData.value[role]) {
    return null
  }
  
  const positionField = `${position.toLowerCase()}_players`
  const playerId = depthChartData.value[role][positionField]
  
  if (!playerId) {
    return null
  }
  
  if (teamData.value && teamData.value.roster) {
    const rosterItem = teamData.value.roster.find(item => {
      const itemId = item.id
      const playerId1 = item.player?.p_player_id
      const playerId2 = item.player?.player_id
      const playerId3 = item.player?.id
      
      return itemId == playerId || 
             playerId1 == playerId || 
             playerId2 == playerId || 
             playerId3 == playerId
    })
    
    if (rosterItem && rosterItem.player) {
      return rosterItem.player.player_name
    }
  }
  
  return `Player ${playerId}`
}

// Open player selection modal
const openPlayerSelection = (position, role) => {
  selectedPosition.value = position
  selectedRole.value = role
  showPlayerSelectionModal.value = true
}

// Select player for depth chart (local only, no backend save)
const selectPlayerForDepthChart = (player) => {
  if (!depthChartData.value || !depthChartData.value[selectedRole.value]) {
    console.error('Depth chart data or selected role not found')
    return
  }
  
  const positionField = `${selectedPosition.value.toLowerCase()}_players`
  const playerId = player.id || player.p_player_id || player.player_id
  
  // Update local data only - no backend persistence
  depthChartData.value[selectedRole.value][positionField] = playerId
  
  // Store values before clearing
  const position = selectedPosition.value
  const role = selectedRole.value
  
  showPlayerSelectionModal.value = false
  selectedPosition.value = null
  selectedRole.value = null
  
  // NOTE: Changes are local only and will not persist to backend
  emit('player-selected', { position, role, player })
}

// Clear player from cell (local only, no backend save)
const clearPlayerFromCell = (position, role) => {
  if (!depthChartData.value || !depthChartData.value[role]) {
    return
  }
  
  const positionField = `${position.toLowerCase()}_players`
  // Update local data only - no backend persistence
  depthChartData.value[role][positionField] = null
  
  // NOTE: Changes are local only and will not persist to backend
  emit('player-cleared', { position, role })
}

// Close player selection modal
const closePlayerSelectionModal = () => {
  showPlayerSelectionModal.value = false
  selectedPosition.value = null
  selectedRole.value = null
}

// Watch for teamId changes
watch(() => props.teamId, (newTeamId) => {
  if (newTeamId) {
    fetchTeamData()
  }
}, { immediate: true })

// Fetch on mount
onMounted(() => {
  if (props.teamId) {
    fetchTeamData()
  }
})

// Expose depth chart data to parent component
defineExpose({
  depthChartData
})
</script>

<template>
  <div class="space-y-4">
    <!-- Depth Chart Container -->
    <div class="bg-white rounded-lg shadow border border-gray-100 p-4">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center">
          <i class="fas fa-table text-blue-500 mr-2"></i>
          <span class="font-semibold text-gray-700">Team Depth Chart</span>
          <span class="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">Local Override (Not Saved)</span>
        </div>
        <div v-if="isLoadingDepthChart" class="flex items-center text-sm text-gray-500">
          <i class="fas fa-spinner fa-spin mr-2"></i>
          Loading depth chart...
        </div>
      </div>
      
      <!-- Depth Chart Table -->
      <div v-if="isLoadingDepthChart" class="flex items-center justify-center h-32 text-gray-500">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-2xl mb-2"></i>
          <p>Loading depth chart...</p>
        </div>
      </div>
      <div v-else-if="!depthChartData" class="flex items-center justify-center h-32 text-gray-500">
        <div class="text-center">
          <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
          <p>No depth chart data available</p>
        </div>
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-50">
              <th class="border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-700">Role</th>
              <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">PG</th>
              <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">SG</th>
              <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">SF</th>
              <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">PF</th>
              <th class="border border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-700">C</th>
            </tr>
          </thead>
          <tbody>
            <!-- Starter Row -->
            <tr class="hover:bg-gray-50">
              <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-green-50">Starter</td>
              <td 
                @click="openPlayerSelection('PG', 'starter')"
                @contextmenu.prevent="clearPlayerFromCell('PG', 'starter')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PG', 'starter')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PG', 'starter') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PG', 'starter')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SG', 'starter')"
                @contextmenu.prevent="clearPlayerFromCell('SG', 'starter')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SG', 'starter')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SG', 'starter') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SG', 'starter')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SF', 'starter')"
                @contextmenu.prevent="clearPlayerFromCell('SF', 'starter')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SF', 'starter')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SF', 'starter') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SF', 'starter')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('PF', 'starter')"
                @contextmenu.prevent="clearPlayerFromCell('PF', 'starter')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PF', 'starter')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PF', 'starter') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PF', 'starter')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('C', 'starter')"
                @contextmenu.prevent="clearPlayerFromCell('C', 'starter')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('C', 'starter')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('C', 'starter') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('C', 'starter')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
            </tr>
            


            <!-- Rotational Player Row -->
            <tr class="hover:bg-gray-50">
              <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-blue-50">Rotational Player</td>
              <td 
                @click="openPlayerSelection('PG', 'rotational')"
                @contextmenu.prevent="clearPlayerFromCell('PG', 'rotational')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PG', 'rotational')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PG', 'rotational') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PG', 'rotational')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SG', 'rotational')"
                @contextmenu.prevent="clearPlayerFromCell('SG', 'rotational')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SG', 'rotational')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SG', 'rotational') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SG', 'rotational')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SF', 'rotational')"
                @contextmenu.prevent="clearPlayerFromCell('SF', 'rotational')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SF', 'rotational')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SF', 'rotational') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SF', 'rotational')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('PF', 'rotational')"
                @contextmenu.prevent="clearPlayerFromCell('PF', 'rotational')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PF', 'rotational')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PF', 'rotational') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PF', 'rotational')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('C', 'rotational')"
                @contextmenu.prevent="clearPlayerFromCell('C', 'rotational')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('C', 'rotational')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('C', 'rotational') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('C', 'rotational')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
            </tr>
                        <!-- In & Out of Rotation Row -->
                        <tr class="hover:bg-gray-50">
              <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-blue-50">In & Out of Rotation</td>
              <td 
                @click="openPlayerSelection('PG', 'in_out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('PG', 'in_out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PG', 'in_out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PG', 'in_out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PG', 'in_out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SG', 'in_out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('SG', 'in_out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SG', 'in_out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SG', 'in_out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SG', 'in_out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SF', 'in_out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('SF', 'in_out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SF', 'in_out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SF', 'in_out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SF', 'in_out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('PF', 'in_out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('PF', 'in_out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PF', 'in_out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PF', 'in_out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PF', 'in_out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('C', 'in_out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('C', 'in_out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('C', 'in_out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('C', 'in_out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('C', 'in_out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
            </tr>
            
            <!-- Injury Backup Row -->
            <tr class="hover:bg-gray-50">
              <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-yellow-50">Injury Backup</td>
              <td 
                @click="openPlayerSelection('PG', 'injury_backup')"
                @contextmenu.prevent="clearPlayerFromCell('PG', 'injury_backup')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PG', 'injury_backup')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PG', 'injury_backup') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PG', 'injury_backup')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SG', 'injury_backup')"
                @contextmenu.prevent="clearPlayerFromCell('SG', 'injury_backup')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SG', 'injury_backup')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SG', 'injury_backup') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SG', 'injury_backup')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SF', 'injury_backup')"
                @contextmenu.prevent="clearPlayerFromCell('SF', 'injury_backup')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SF', 'injury_backup')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SF', 'injury_backup') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SF', 'injury_backup')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('PF', 'injury_backup')"
                @contextmenu.prevent="clearPlayerFromCell('PF', 'injury_backup')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PF', 'injury_backup')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PF', 'injury_backup') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PF', 'injury_backup')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('C', 'injury_backup')"
                @contextmenu.prevent="clearPlayerFromCell('C', 'injury_backup')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('C', 'injury_backup')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('C', 'injury_backup') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('C', 'injury_backup')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
            </tr>
            
            <!-- Injury Backup 2 Row -->
            <tr class="hover:bg-gray-50">
              <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-yellow-50">Injury Backup 2</td>
              <td 
                @click="openPlayerSelection('PG', 'injury_backup_2')"
                @contextmenu.prevent="clearPlayerFromCell('PG', 'injury_backup_2')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PG', 'injury_backup_2')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PG', 'injury_backup_2') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PG', 'injury_backup_2')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SG', 'injury_backup_2')"
                @contextmenu.prevent="clearPlayerFromCell('SG', 'injury_backup_2')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SG', 'injury_backup_2')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SG', 'injury_backup_2') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SG', 'injury_backup_2')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SF', 'injury_backup_2')"
                @contextmenu.prevent="clearPlayerFromCell('SF', 'injury_backup_2')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SF', 'injury_backup_2')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SF', 'injury_backup_2') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SF', 'injury_backup_2')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('PF', 'injury_backup_2')"
                @contextmenu.prevent="clearPlayerFromCell('PF', 'injury_backup_2')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PF', 'injury_backup_2')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PF', 'injury_backup_2') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PF', 'injury_backup_2')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('C', 'injury_backup_2')"
                @contextmenu.prevent="clearPlayerFromCell('C', 'injury_backup_2')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('C', 'injury_backup_2')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('C', 'injury_backup_2') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('C', 'injury_backup_2')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
            </tr>
            
            <!-- Injury Backup 3 Row -->
            <tr class="hover:bg-gray-50">
              <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-yellow-50">Injury Backup 3</td>
              <td 
                @click="openPlayerSelection('PG', 'injury_backup_3')"
                @contextmenu.prevent="clearPlayerFromCell('PG', 'injury_backup_3')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PG', 'injury_backup_3')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PG', 'injury_backup_3') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PG', 'injury_backup_3')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SG', 'injury_backup_3')"
                @contextmenu.prevent="clearPlayerFromCell('SG', 'injury_backup_3')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SG', 'injury_backup_3')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SG', 'injury_backup_3') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SG', 'injury_backup_3')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SF', 'injury_backup_3')"
                @contextmenu.prevent="clearPlayerFromCell('SF', 'injury_backup_3')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SF', 'injury_backup_3')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SF', 'injury_backup_3') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SF', 'injury_backup_3')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('PF', 'injury_backup_3')"
                @contextmenu.prevent="clearPlayerFromCell('PF', 'injury_backup_3')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PF', 'injury_backup_3')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PF', 'injury_backup_3') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PF', 'injury_backup_3')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('C', 'injury_backup_3')"
                @contextmenu.prevent="clearPlayerFromCell('C', 'injury_backup_3')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('C', 'injury_backup_3')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('C', 'injury_backup_3') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('C', 'injury_backup_3')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
            </tr>
            

            <!-- Out of Rotation Row -->
            <tr class="hover:bg-gray-50">
              <td class="border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 bg-red-50">Out of Rotation</td>
              <td 
                @click="openPlayerSelection('PG', 'out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('PG', 'out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PG', 'out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PG', 'out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PG', 'out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SG', 'out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('SG', 'out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SG', 'out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SG', 'out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SG', 'out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('SF', 'out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('SF', 'out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('SF', 'out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('SF', 'out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('SF', 'out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('PF', 'out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('PF', 'out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('PF', 'out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('PF', 'out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('PF', 'out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
              <td 
                @click="openPlayerSelection('C', 'out_rotation')"
                @contextmenu.prevent="clearPlayerFromCell('C', 'out_rotation')"
                class="border border-gray-200 px-3 py-2 text-sm text-center text-gray-800 cursor-pointer hover:bg-blue-50 transition-colors relative group"
              >
                <div v-if="getDepthChartPlayer('C', 'out_rotation')" class="text-xs relative">
                  <span>{{ getDepthChartPlayer('C', 'out_rotation') }}</span>
                  <button 
                    @click.stop="clearPlayerFromCell('C', 'out_rotation')"
                    class="absolute right-0 top-0 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    title="Remove player"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div v-else class="text-gray-400 text-xs">Click to select</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Player Selection Modal -->
    <div
      v-if="showPlayerSelectionModal"
      class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      @click="closePlayerSelectionModal"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">
              Select Player for {{ selectedPosition }} - {{ selectedRole }}
            </h3>
            <p class="text-sm text-gray-600 mt-1">
              Choose a player from the current team roster
            </p>
            <p class="text-xs text-orange-600 mt-1">
              Note: Changes are local only and will not be saved to the backend
            </p>
          </div>
          <button
            @click="closePlayerSelectionModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>

        <!-- Modal Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="teamData && teamData.roster && teamData.roster.length > 0" class="space-y-2">
            <div
              v-for="rosterItem in teamData.roster"
              :key="rosterItem.id"
              @click="selectPlayerForDepthChart(rosterItem.player)"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors border border-gray-200"
            >
              <div class="flex items-center space-x-3">
                <i class="fas fa-user text-blue-500"></i>
                <div>
                  <div class="font-medium text-gray-800">
                    {{ rosterItem.player?.player_name || 'Unknown Player' }}
                  </div>
                  <div class="text-sm text-gray-600">
                    <span v-if="rosterItem.player?.position">{{ rosterItem.player.position }}</span>
                    <span v-if="rosterItem.player?.position && rosterItem.player?.nationality"> • </span>
                    <span v-if="rosterItem.player?.nationality">{{ rosterItem.player.nationality }}</span>
                  </div>
                </div>
              </div>
              <div class="text-sm text-gray-500">
                ID: {{ rosterItem.player?.p_player_id || rosterItem.id }}
              </div>
            </div>
          </div>
          <div v-else class="text-center text-gray-500 py-8">
            <i class="fas fa-users text-3xl mb-4"></i>
            <p>No players available in the team roster</p>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            @click="closePlayerSelectionModal"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
